import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import {
  Button,
  IconButton,
  TextField,
  MuiThemeProvider,
  createMuiTheme,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { useHistory, useParams } from 'react-router-dom'
import {
  deleteGamificationAudioguide,
  getGamificationAudioguideById,
  postGamificationAudioguide,
  updateGamificationAudioguide,
} from '../../../../api/gamificationAudioguide'
import { useSkeleton } from '../../../hooks/useSkeleton'
import { alertError, alertSuccess } from '../../../../utils/logger'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'
import MultilanguageTabBlock from '../../../components/MultilanguageTabBlock'

const theme = createMuiTheme({
  palette: { secondary: { main: '#F64E60' } },
})

function toLocalizedMap(value) {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? { es: trimmed } : {}
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return { ...value }
  }
  return {}
}

function emptyLink() {
  return {
    name: {},
    url: '',
    descripcion: {},
    boton: {},
  }
}

function normalizeLoadedLink(raw) {
  const src = raw && typeof raw === 'object' ? raw : {}
  return {
    name: toLocalizedMap(src.name),
    url: src.url || '',
    descripcion: toLocalizedMap(src.descripcion),
    boton: toLocalizedMap(src.boton),
  }
}

function getEmpty() {
  return {
    id_pid: '',
    name_lugar: '',
    links: [emptyLink()],
  }
}

function looksLikeUrl(value) {
  const trimmed = String(value || '').trim()
  if (!trimmed) return true
  return /^(https?:\/\/)?[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(trimmed)
}

export default function EditGamificationAudioguidePage() {
  const [item, setItem] = useState(getEmpty())
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const itemId = useParams().id
  const history = useHistory()
  const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } = useSkeleton()

  useEffect(() => {
    if (!itemId) {
      disableLoadingData()
      return
    }
    getGamificationAudioguideById(itemId)
      .then((res) => {
        if (res.status === 200) {
          const links = Array.isArray(res.data.links) && res.data.links.length
            ? res.data.links.map(normalizeLoadedLink)
            : [emptyLink()]
          setItem({
            ...getEmpty(),
            ...res.data,
            name_lugar: res.data.name_lugar || '',
            links,
          })
          disableLoadingData()
        }
      })
      .catch((error) => {
        alertError({ error, customMessage: 'No se pudo cargar el registro.' })
        history.push('/gamification-audioguides')
      })
  }, [itemId, disableLoadingData, history])

  function patchLink(index, field, value) {
    setItem((prev) => ({
      ...prev,
      links: (prev.links || []).map((link, i) =>
        i === index ? { ...link, [field]: value } : link,
      ),
    }))
  }

  function patchLinkLocalized(index, field, lang, value) {
    setItem((prev) => ({
      ...prev,
      links: (prev.links || []).map((link, i) =>
        i === index
          ? { ...link, [field]: { ...(link[field] || {}), [lang]: value } }
          : link,
      ),
    }))
  }

  function addLink() {
    setItem((prev) => ({
      ...prev,
      links: [...(prev.links || []), emptyLink()],
    }))
  }

  function removeLink(index) {
    setItem((prev) => {
      const next = (prev.links || []).filter((_, i) => i !== index)
      return { ...prev, links: next.length ? next : [emptyLink()] }
    })
  }

  function save() {
    if (!item.id_pid || !item.name_lugar) {
      alertError({ error: null, customMessage: 'id_pid y name_lugar son obligatorios.' })
      return
    }
    const links = (item.links || []).filter((link) => String(link.url || '').trim())
    if (links.some((link) => !looksLikeUrl(link.url))) {
      alertError({ error: null, customMessage: 'Revisa que las URLs sean válidas.' })
      return
    }
    const payload = {
      id_pid: item.id_pid,
      name_lugar: item.name_lugar,
      links,
    }
    const req = itemId
      ? updateGamificationAudioguide(itemId, payload)
      : postGamificationAudioguide(payload)
    req
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          alertSuccess({ title: 'Guardado', customMessage: 'Registro guardado.' })
          history.push('/gamification-audioguides')
        }
      })
      .catch((error) => {
        const status = error?.response?.status
        const message =
          status === 409
            ? 'Ya existe un registro con ese id_pid.'
            : 'No se pudo guardar.'
        alertError({ error, customMessage: message })
      })
  }

  if (isLoadingData) return <ContentSkeleton />

  return (
    <>
      <Card>
        <CardHeader title={itemId ? 'Editar gamificación / audioguía' : 'Nueva gamificación / audioguía'} />
        <CardBody>
          <TextField
            label="id_pid"
            value={item.id_pid || ''}
            onChange={(e) => setItem({ ...item, id_pid: e.target.value })}
            margin="normal"
            variant="outlined"
            fullWidth
            required
            helperText="Identificador PID del recurso (último segmento de la URI, p. ej. 5V-M2-088)"
          />
          <TextField
            label="name_lugar"
            value={item.name_lugar || ''}
            onChange={(e) => setItem({ ...item, name_lugar: e.target.value })}
            margin="normal"
            variant="outlined"
            fullWidth
            required
            helperText="Nombre del recurso (p. ej. Castillo de Uncastillo)"
          />
          {(item.links || []).map((link, index) => (
            <div
              key={index}
              style={{
                marginTop: 24,
                padding: 16,
                border: '1px solid #e2ddd4',
                borderRadius: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 style={{ margin: 0 }}>Enlace {index + 1}</h4>
                <IconButton onClick={() => removeLink(index)} aria-label="Eliminar enlace">
                  <DeleteIcon />
                </IconButton>
              </div>
              <TextField
                label="URL"
                value={link.url || ''}
                onChange={(e) => patchLink(index, 'url', e.target.value)}
                margin="normal"
                variant="outlined"
                fullWidth
              />
              <MultilanguageTabBlock
                multilanguageTabContent={(lang) => (
                  <>
                    <TextField
                      label="Nombre (título)"
                      value={(link.name && link.name[lang]) || ''}
                      onChange={(e) => patchLinkLocalized(index, 'name', lang, e.target.value)}
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Descripción"
                      value={(link.descripcion && link.descripcion[lang]) || ''}
                      onChange={(e) =>
                        patchLinkLocalized(index, 'descripcion', lang, e.target.value)
                      }
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Botón"
                      value={(link.boton && link.boton[lang]) || ''}
                      onChange={(e) => patchLinkLocalized(index, 'boton', lang, e.target.value)}
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  </>
                )}
              />
            </div>
          ))}
          <Button onClick={addLink} variant="outlined" color="primary" style={{ marginTop: 16 }}>
            Añadir enlace
          </Button>
        </CardBody>
      </Card>
      <Button onClick={() => history.push('/gamification-audioguides')} variant="outlined" style={{ marginRight: 20 }}>
        Volver
      </Button>
      <Button onClick={save} variant="outlined" color="primary" style={{ marginRight: 20 }}>
        Guardar
      </Button>
      {itemId && (
        <>
          <MuiThemeProvider theme={theme}>
            <Button onClick={() => setOpenConfirmDialog(true)} variant="outlined" color="secondary">
              Eliminar
            </Button>
          </MuiThemeProvider>
          <ConfirmDialog
            title="¿Eliminar este registro?"
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteGamificationAudioguide(itemId)
                .then((res) => {
                  if (res.status === 200 || res.status === 204) {
                    alertSuccess({ title: 'Eliminado', customMessage: 'Registro eliminado.' })
                    history.push('/gamification-audioguides')
                  }
                })
                .catch((error) => {
                  alertError({ error, customMessage: 'No se pudo eliminar.' })
                })
            }}
          />
        </>
      )}
    </>
  )
}
